/**
 * Unified card/die acquisition drawer (HEX-149; generalises the HEX-147 make-room
 * drawer).
 *
 * Mounts the `InventoryEditor` in **offer mode**: the incoming item shows beside
 * the loadout so the player can drag a card/die out (destroyed — frees the cell)
 * and place the new one in, or decline it. One surface for every gain source
 * (combat drop, chest, cache, shop/merchant buy, level-up pick) — no full-cover
 * modal hiding the loadout, no fork of a second curation path.
 *
 * Edits write straight back onto the character (`character.inventory`) as the
 * player curates — destructions are permanent (run-scoped) the moment they
 * happen, so a back-out still keeps them. The promise resolves once the player
 * resolves the offer:
 *   - `'placed'`  — the item now sits in the loadout (the host charges / persists)
 *   - `'declined'` — the item is forgone (the host takes gold / refunds nothing)
 * The host owns persistence on resolve.
 */

import type { Character } from '../../core/character';
import type { Card } from '../types';
import { InventoryEditor } from './InventoryEditor';

export interface AcquisitionRequest {
  /** The incoming item — a card, or a die already wrapped via `dieCard`. */
  readonly item: Card;
  /** Label for the decline action (e.g. "Take 12 gold", "Leave it", "Forgo"). */
  readonly declineLabel: string;
}

export type AcquisitionOutcome = 'placed' | 'declined';

export interface AcquisitionSession {
  /** Resolves once the player places or declines the item. */
  readonly done: Promise<AcquisitionOutcome>;
  /** Tear the drawer down without resolving `done` — the host is abandoning the
   * flow (mirrors the old DropPrompt.destroy: drop the DOM, leave the await
   * dangling on a flow that is itself being destroyed). */
  destroy(): void;
}

/** Open the acquisition drawer for one incoming item. The returned session
 * carries the outcome promise + a teardown for host `destroy()` paths. */
export function openAcquisition(
  container: HTMLElement,
  character: Character,
  request: AcquisitionRequest,
): AcquisitionSession {
  const editor = new InventoryEditor(character.inventory);
  let settled = false;
  let resolveDone: (outcome: AcquisitionOutcome) => void = () => {};
  const done = new Promise<AcquisitionOutcome>((resolve) => {
    resolveDone = resolve;
  });
  editor.mount(container, {
    inventory: character.inventory,
    onChange: (inv) => {
      character.inventory = inv;
    },
    offer: {
      item: request.item,
      declineLabel: request.declineLabel,
      onResolve: (outcome) => {
        if (settled) return;
        settled = true;
        editor.destroy();
        resolveDone(outcome);
      },
    },
  });
  return {
    done,
    destroy: () => {
      if (settled) return;
      settled = true;
      editor.destroy();
    },
  };
}
